import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { GmAssignmentService } from '@org/shared-services';
import { Tenant, GetMyAssignmentsResponse, SwitchToTenantRequest, SwitchToTenantResponse } from '@org/interfaces';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-gm-location-switcher',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <!-- Desktop Version -->
    <div *ngIf="assignedTenants.length > 1" class="hidden md:flex items-center gap-3">
      <div class="flex items-center gap-2">
        <svg class="h-5 w-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
        </svg>
        <select [formControl]="tenantControl" id="tenantSwitcher" 
                class="bg-gray-600 border border-gray-500 text-white text-sm rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[180px]">
          <option *ngFor="let tenant of assignedTenants" [value]="tenant._id">{{ tenant.name }}</option>
        </select>
      </div>
      <span *ngIf="isSwitching" class="text-sm text-gray-400 flex items-center">
        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Switching...
      </span>
    </div>

    <!-- Mobile Version. Hidden unless this GM manages more than one location. -->
    <div *ngIf="assignedTenants.length > 1" class="md:hidden border-b border-gray-700 px-2 pb-3 pt-2 sm:px-3">
      <div class="mb-2 flex items-center space-x-2">
        <svg class="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
        </svg>
        <span class="text-sm font-semibold text-gray-200">Location Manager</span>
      </div>
      <div class="flex flex-col space-y-2">
        <label for="tenantSwitcherMobile" class="text-sm font-medium text-gray-300">Current Location</label>
        <div class="flex items-center space-x-2">
          <select [formControl]="tenantControl" id="tenantSwitcherMobile" 
                  class="flex-1 bg-gray-600 border border-gray-500 text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            <option *ngFor="let tenant of assignedTenants" [value]="tenant._id">{{ tenant.name }}</option>
          </select>
          <div *ngIf="isSwitching" class="flex items-center justify-center w-8 h-8">
            <svg class="animate-spin h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        </div>
        <div *ngIf="isSwitching" class="text-xs text-gray-400 text-center">Switching to new location...</div>
      </div>
    </div>
  `,
  styles: [`
    /* Custom select styling for better mobile experience */
    select {
      appearance: none;
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
      background-position: right 0.5rem center;
      background-repeat: no-repeat;
      background-size: 1.5em 1.5em;
      padding-right: 2.5rem;
    }
    
    /* Focus states for better accessibility */
    select:focus {
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
    }
    
    /* Mobile-specific optimizations */
    @media (max-width: 768px) {
      select {
        font-size: 16px; /* Prevents zoom on iOS */
      }
    }
  `]
})
export class GmLocationSwitcherComponent implements OnInit {
  assignedTenants: Tenant[] = [];
  tenantControl = new FormControl('');
  currentTenantId: string | null = null;
  isSwitching = false;

  @Output() tenantSwitched = new EventEmitter<Tenant>();

  constructor(private gmAssignmentService: GmAssignmentService) {}

  ngOnInit() {
    this.gmAssignmentService.getMyAssignments().subscribe((res: GetMyAssignmentsResponse) => {
        console.log("res",res);
      if (res.data && Array.isArray(res.data.assignments) && res.data.assignments.length > 0) {
        this.assignedTenants = res.data.assignments
          .map(a => a.assignedTenant)
          .filter((t): t is Tenant => !!t && !!t._id && !!t.name);
        const activeAssignment = res.data.assignments.find(a => a.isCurrentlyActive);
        const primaryAssignment = res.data.assignments.find(a => a.isPrimaryTenant);
        this.currentTenantId = res.data.currentActiveTenant || 
                       activeAssignment?.assignedTenant._id ||
                       primaryAssignment?.assignedTenant._id ||
                       null;
        // Debug logging
        console.log('primaryTenant:', res.data.primaryTenant);
        console.log('assignedTenants:', this.assignedTenants);
        console.log('currentTenantId:', this.currentTenantId);
        this.tenantControl.setValue(this.currentTenantId, { emitEvent: false });
      } else {
        this.assignedTenants = [];
        this.currentTenantId = null;
      }
    });
    this.tenantControl.valueChanges.subscribe((tenantId: string | null) => {
      if (!tenantId || tenantId === this.currentTenantId) return;
      this.isSwitching = true;
      const payload: SwitchToTenantRequest = { tenantId };
      this.gmAssignmentService.switchToTenant(payload).subscribe((res: SwitchToTenantResponse) => {
        
        this.currentTenantId = tenantId;
        const switchedTenant = this.assignedTenants.find(t => t._id === tenantId);
        if (switchedTenant) {
          this.tenantSwitched.emit(switchedTenant);
        }
        // Reload the page to ensure all context is updated
        window.location.reload();
        this.isSwitching = false;
      }, () => {
        this.isSwitching = false;
      });
    });
  }
} 